'use client'

// BAO-125: the marketing CTA link.
//
// Phase 5b (this lane): with ads consent, a click sends GA `generate_lead` and
// Meta `Lead` with one shared `event_id` (so a later server-side send can
// dedupe), waits for gtag's `event_callback` or 300 ms, then navigates.
// GaRouteGuard silences GA the moment the route changes, so the beacon must
// leave before the navigation, not after. Without ads consent it is a plain
// link.
//
// Phase 3 (Lane E) adds the PostHog `marketing_cta_clicked` capture. Unlike
// the ad beacons it is NOT gated on ads consent: it is first-party product
// analytics, and an analytics decline already silences it at the posthog-js
// level (`opt_out_capturing`, Phase 5a). It also does not delay navigation -
// posthog-js queues to its own transport, so only the ad pixels need the
// `event_callback` wait.
import * as React from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useConsent } from '@/lib/analytics/use-consent'
import { captureClientFunnelEvent, type CtaLocation } from '@/lib/analytics/client-events'

/**
 * `useRouter` throws outside an app router (component tests that render the
 * page tree directly). Navigation then falls back to a full load, which is
 * still correct - the beacon has already left by then.
 */
function useOptionalRouter(): { push: (href: string) => void } | null {
  try {
    return useRouter()
  } catch {
    return null
  }
}

export const LEAD_NAVIGATION_TIMEOUT_MS = 300

interface TrackedSignupLinkProps extends Omit<React.ComponentProps<typeof Link>, 'href' | 'onClick'> {
  href?: string
  ctaLocation: CtaLocation
  /** Copy variant shown (e.g. `trial` vs `pay`), for later slicing. */
  ctaVariant?: string
}

function newEventId(): string {
  try {
    return crypto.randomUUID()
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`
  }
}

/** Fire the ad-platform lead events; resolves when it is safe to navigate. */
export function sendLeadSignals(
  win: Pick<Window, 'gtag' | 'fbq'>,
  props: { cta_location: string; cta_variant?: string; page: string },
  timeoutMs = LEAD_NAVIGATION_TIMEOUT_MS,
): Promise<string> {
  const eventId = newEventId()
  return new Promise(resolve => {
    let done = false
    const finish = () => {
      if (done) return
      done = true
      resolve(eventId)
    }
    const timer = setTimeout(finish, timeoutMs)
    try {
      win.fbq?.('track', 'Lead', { content_name: props.cta_location }, { eventID: eventId })
    } catch {
      // third-party code
    }
    try {
      if (typeof win.gtag === 'function') {
        win.gtag('event', 'generate_lead', {
          event_id: eventId,
          cta_location: props.cta_location,
          cta_variant: props.cta_variant,
          page: props.page,
          event_callback: () => {
            clearTimeout(timer)
            finish()
          },
        })
      }
    } catch {
      // third-party code
    }
  })
}

export function TrackedSignupLink({
  href = '/signup',
  ctaLocation,
  ctaVariant,
  children,
  ...rest
}: TrackedSignupLinkProps) {
  const router = useOptionalRouter()
  const { state, hydrated } = useConsent()
  const adsGranted = hydrated && state.effective.ads === 'granted'

  /**
   * The ad beacons, and only them, are gated on ads consent. Split out so the
   * rule is a property of this function rather than of the ORDER of statements
   * in the click handler - the thing a later refactor is most likely to
   * destroy without any test noticing.
   */
  const maybeSendLeadSignals = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!adsGranted) return
    const props = {
      cta_location: ctaLocation,
      cta_variant: ctaVariant,
      page: window.location.pathname,
    }
    // Modified clicks (new tab) keep the browser's behaviour; the beacon
    // still goes out with the page alive.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
      void sendLeadSignals(window, props)
      return
    }
    e.preventDefault()
    void sendLeadSignals(window, props).then(() => {
      if (router) router.push(href)
      else window.location.assign(href)
    })
  }

  const onClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Unconditional: first-party product analytics, never an ad signal. A
    // visitor who declined ad cookies still belongs in the funnel.
    captureClientFunnelEvent('marketing_cta_clicked', {
      cta_location: ctaLocation,
      page: window.location.pathname,
      cta_variant: ctaVariant,
    })
    maybeSendLeadSignals(e)
  }

  return (
    <Link href={href} onClick={onClick} data-cta-location={ctaLocation} {...rest}>
      {children}
    </Link>
  )
}
